import { useMemo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  ModernAppShell,
  ModernLeftRail,
  ModernSecondaryNav,
  ModernTopBar,
  ModernButton,
  type ModernRailItem,
  type ModernNavGroup,
} from '@command-center/oneup-modern-ui';
import { computeQueueCounts } from '@oneup/shared';
import { tasksApi } from '@/lib/api';
import { AlicePanel } from '@/components/alice';
import { CommandPalette } from '@/components/command-palette';
import { CreateRecordDrawer } from '@/components/create-record-drawer';
import { EmailComposerModal } from '@/components/email-composer-modal';
import { GlobalNotice } from '@/components/global-notice';
import { GlobalShortcuts } from '@/components/global-shortcuts';
import { useUiStore } from '@/stores/ui';
import { getBranding } from '@/platform/branding';
import { getNavigationGroups } from '@/platform/navigation';
import { GROUP_RAIL_ICONS } from './group-icons';

export function ModernAppShellAdapter({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const branding = useMemo(() => getBranding(), []);
  const navGroups = useMemo(() => getNavigationGroups(), []);

  const emailComposerOpen = useUiStore((state) => state.emailComposerOpen);
  const emailComposerInitial = useUiStore((state) => state.emailComposerInitial);
  const setEmailComposerOpen = useUiStore((state) => state.setEmailComposerOpen);
  const openCreateDrawer = useUiStore((state) => state.openCreateDrawer);
  const openCommandPalette = useUiStore((state) => state.openCommandPalette);

  // Shares cache with TasksView — no double-fetch.
  const { data } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: () => tasksApi.list({ limit: 300 }),
  });
  const counts = useMemo(() => computeQueueCounts(data?.items ?? []), [data?.items]);

  // Find which group contains the /tasks route for the sidebar badge.
  const tasksGroupLabel = useMemo(() => {
    for (const group of navGroups) {
      if (group.items.some((item) => item.to === '/tasks')) return group.label;
    }
    return null;
  }, [navGroups]);

  // One rail item per non-empty nav group.
  const railItems = useMemo<ModernRailItem[]>(
    () =>
      navGroups
        .filter((g) => g.items.length > 0)
        .map((group) => ({
          id: group.label,
          label: group.label,
          icon: GROUP_RAIL_ICONS[group.label] ?? <span className="block h-5 w-5" />,
          badge:
            group.label === tasksGroupLabel && counts.today ? counts.today : undefined,
        })),
    [navGroups, tasksGroupLabel, counts.today],
  );

  // The active rail group is whichever one owns the current pathname.
  const activeGroupLabel = useMemo(() => {
    for (const group of navGroups) {
      for (const item of group.items) {
        if (pathname === item.to || pathname.startsWith(item.to + '/')) {
          return group.label;
        }
      }
    }
    return navGroups[0]?.label ?? '';
  }, [navGroups, pathname]);

  const activeGroup = useMemo(
    () => navGroups.find((g) => g.label === activeGroupLabel) ?? navGroups[0],
    [navGroups, activeGroupLabel],
  );

  // Secondary nav shows the items of the currently active group.
  const secondaryNavGroups = useMemo<ModernNavGroup[]>(() => {
    if (!activeGroup) return [];
    return [
      {
        id: activeGroup.label,
        items: activeGroup.items
          .filter((item) => !item.placeholder)
          .map((item) => {
            const Icon = item.icon;
            return {
              id: item.id,
              label: item.label,
              icon: <Icon className="h-4 w-4" />,
              href: item.to,
            };
          }),
      },
    ];
  }, [activeGroup]);

  const activeNavId = useMemo(() => {
    for (const item of activeGroup?.items ?? []) {
      if (pathname === item.to || pathname.startsWith(item.to + '/')) {
        return item.id;
      }
    }
    return undefined;
  }, [activeGroup, pathname]);

  const handleRailSelect = (id: string) => {
    const group = navGroups.find((g) => g.label === id);
    const first = group?.items.find((i) => !i.placeholder);
    if (first) navigate(first.to);
  };

  const handleNavSelect = (id: string) => {
    const item = activeGroup?.items.find((i) => i.id === id);
    if (item?.to) navigate(item.to);
  };

  return (
    <div className="h-screen overflow-hidden">
      <ModernAppShell
        rail={
          <ModernLeftRail
            brand={
              <span className="flex h-9 w-9 items-center justify-center rounded-[calc(var(--radius)-4px)] bg-primary text-[13px] font-bold text-primary-foreground">
                {branding.iconText}
              </span>
            }
            items={railItems}
            activeId={activeGroupLabel}
            onSelect={handleRailSelect}
          />
        }
        secondaryNav={
          <ModernSecondaryNav
            header={
              <div>
                <div className="text-sm font-semibold">{activeGroupLabel}</div>
                <div className="text-[11px] text-muted-foreground">{branding.shortLabel}</div>
              </div>
            }
            groups={secondaryNavGroups}
            activeId={activeNavId}
            onSelect={handleNavSelect}
          />
        }
        topBar={
          <ModernTopBar
            searchPlaceholder="Search & Command"
            onSearchClick={openCommandPalette}
            actions={
              <ModernButton
                size="sm"
                variant="primary"
                onClick={() => openCreateDrawer({ type: 'task' })}
              >
                <Plus className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Quick Capture</span>
              </ModernButton>
            }
          />
        }
        assistant={<AlicePanel />}
        className="h-screen"
      >
        {children}
      </ModernAppShell>

      <GlobalShortcuts />
      <CommandPalette />
      <CreateRecordDrawer />
      <EmailComposerModal
        open={emailComposerOpen}
        initial={emailComposerInitial}
        onOpenChange={setEmailComposerOpen}
      />
      <GlobalNotice />
    </div>
  );
}

